import { useId, useMemo } from 'react'
import { Clock, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { DropdownSelect, type DropdownOption } from './DropdownSelect'

function renderLabel(text: string) {
  const trimmed = text.trimEnd()
  if (!trimmed.endsWith('*')) return text
  return (
    <>
      {trimmed.slice(0, -1).trimEnd()}
      <span className="ml-0.5 text-red-500">*</span>
    </>
  )
}

const HOUR_OPTIONS: DropdownOption[] = Array.from({ length: 12 }, (_, i) => {
  const h = i + 1
  return { value: String(h), label: String(h).padStart(2, '0') }
})

const MINUTE_OPTIONS: DropdownOption[] = Array.from({ length: 60 }, (_, i) => ({
  value: String(i).padStart(2, '0'),
  label: String(i).padStart(2, '0'),
}))

const PERIOD_OPTIONS: DropdownOption[] = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]

/** Convert 12h picker state -> 24h "HH:mm" string used by the app/backend. */
function to24(hour12: number, minute: string, period: 'AM' | 'PM') {
  let h = hour12 % 12
  if (period === 'PM') h += 12
  return `${String(h).padStart(2, '0')}:${minute}`
}

/** Convert a 24h "HH:mm" string -> 12h picker state. Tolerant of empty/malformed input. */
function from24(value: string) {
  const [hStr, mStr] = (value || '00:00').split(':')
  let h = parseInt(hStr, 10)
  if (Number.isNaN(h)) h = 0
  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM'
  let hour12 = h % 12
  if (hour12 === 0) hour12 = 12
  const mNum = parseInt(mStr, 10)
  const minute = String(Number.isNaN(mNum) ? 0 : mNum).padStart(2, '0')
  return { hour12, minute, period }
}

/* Widths are floors derived from DropdownSelect's own chrome, not guesses,
   and they have to track `size` because the chrome shrinks with it:

     md  px-3.5 (28) + gap-2 (8) + chevron (14)            = 50 before text
         hour also carries a leftIcon + its gap            = 71
     sm  px-3   (24) + gap-2 (8) + chevron (14)            = 46
         hour also carries a leftIcon + its gap            = 67

   The label span truncates, so anything narrower silently eats the value.
   Each width below leaves room for the widest label that control shows
   ("12", "00", "AM") at that size's font. Row totals: ~265px at md,
   ~234px at sm, so both still fit a 320px viewport.

   Buy space here, not off the leftIcon: the icon is on the 13px scale every
   other leftIcon in the app uses, and shrinking it to claw back a few pixels
   makes a lucide glyph at strokeWidth 2 read as a smudge. The md hour is the
   only control that was actually tight, so it carries the extra width. */
const WIDTHS = {
  md: { hour: 'w-[98px]', minute: 'w-[74px]', period: 'w-[76px]' },
  sm: { hour: 'w-[84px]', minute: 'w-[66px]', period: 'w-[68px]' },
} as const

interface TimeInputProps {
  label?: string
  error?: string
  hint?: string
  value: string
  onChange: (e: { target: { value: string } }) => void
  className?: string
  id?: string
  disabled?: boolean
  size?: 'sm' | 'md'
  /** Names the control when there is no visible `label` (the three
   *  dropdowns are buttons, so without this the group is unnamed). */
  ariaLabel?: string
  /** QA6 SET2-6: an optional time. Shows a clear button that empties a
   *  set value back to blank (empty values always render blank). */
  clearable?: boolean
}

export function TimeInput({
  label,
  error,
  hint,
  value,
  onChange,
  className,
  id,
  disabled,
  size = 'md',
  ariaLabel,
  clearable = false,
}: TimeInputProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const labelId = `${inputId}-label`
  const widths = WIDTHS[size]
  const { hour12, minute, period } = useMemo(() => from24(value), [value])
  // An unset time reads as blank ("--"), not as a real 12:00 AM (QA6 OPS2-37);
  // `clearable` additionally lets the user reset a set time back to blank
  // (QA6 SET2). Picking any part fills the rest with 12 / 00 / AM.
  const empty = !value
  // NEW-B28: each part needs its own accessible name; without one the
  // buttons were announced by their "--" placeholder.
  const partName = (ariaLabel || label?.replace(/\*\s*$/, '').trim() || 'Time').trim()
  function emit(nextHour: number, nextMinute: string, nextPeriod: 'AM' | 'PM') {
    onChange({ target: { value: to24(nextHour, nextMinute, nextPeriod) } })
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* A plain span, not a <label htmlFor>: the three controls are
          buttons rendered by DropdownSelect with no ids of their own, so
          htmlFor pointed at an element that never existed and clicking the
          label focused nothing. aria-labelledby on the group names all
          three at once, which is what a screen reader should announce. */}
      {label && (
        <span id={labelId} className="text-sm font-medium text-gray-700">
          {renderLabel(label)}
        </span>
      )}

      <div
        className="flex min-w-0 flex-wrap items-center gap-1"
        id={inputId}
        role="group"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : ariaLabel}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
      >
        <DropdownSelect
          className={widths.hour}
          size={size}
          value={empty ? '' : String(hour12)}
          placeholder="--"
          onChange={(v) => emit(Number(v), minute, period)}
          options={HOUR_OPTIONS}
          ariaLabel={`${partName} hour`}
          leftIcon={<Clock size={13} />}
          hasError={!!error}
          disabled={disabled}
        />
        <span className="text-sm font-semibold text-gray-300">:</span>
        <DropdownSelect
          className={widths.minute}
          size={size}
          value={empty ? '' : minute}
          placeholder="--"
          onChange={(v) => emit(hour12, v, period)}
          options={MINUTE_OPTIONS}
          ariaLabel={`${partName} minute`}
          hasError={!!error}
          disabled={disabled}
        />
        <DropdownSelect
          className={widths.period}
          size={size}
          value={empty ? '' : period}
          placeholder="--"
          onChange={(v) => emit(hour12, minute, v as 'AM' | 'PM')}
          options={PERIOD_OPTIONS}
          ariaLabel={`${partName} AM or PM`}
          hasError={!!error}
          disabled={disabled}
        />
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={() => onChange({ target: { value: '' } })}
            aria-label="Clear time"
            title="Clear time"
            className="rounded-md p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-500"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {error && (
        <p id={`${inputId}-error`} className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={`${inputId}-hint`} className="text-xs text-gray-500">
          {hint}
        </p>
      )}
    </div>
  )
}
